import { useEffect } from 'react';
import { CoralogixLogSeverity, CoralogixRum } from '@coralogix/browser';
import { CustomTracer, GlobalSpan } from '@coralogix/browser/src/custom-spans';
import {
    ParentSpan,
    AnalyticsAttributes,
    LogAttributes,
    InstrumentTraceType,
    LogLevel,
    LogSeverity,
    MetricAttributes,
    TraceType,
} from './attributes';
import { setUserContext, updateLabels } from './coralogix';

let tracer: CustomTracer | undefined;
let parentSpan: GlobalSpan | undefined;

export const createTrace = (): boolean => {
    if (!tracer) {
        tracer = CoralogixRum.getCustomTracer();
    }

    if (!tracer) {
        CoralogixRum.log(CoralogixLogSeverity.Warn, 'Tracer not initialized');
        return false;
    }

    if (parentSpan) {
        parentSpan.endSpan();
    }

    parentSpan = tracer.startGlobalSpan(ParentSpan, {
        [TraceType]: InstrumentTraceType.ParentSpan,
    });

    if (!parentSpan) {
        CoralogixRum.log(CoralogixLogSeverity.Warn, 'Parent span not initialized');
        return false;
    }

    return true;
};

export const closeTrace = (): void => {
    if (parentSpan) {
        parentSpan.endSpan();
        parentSpan = undefined;
    }
};

export function addAnalytics(
    analyticsAttributes: AnalyticsAttributes,
    options: { persist: true; useCurrentTrace?: boolean }
): { endAnalytics: () => void };
export function addAnalytics(
    analyticsAttributes: AnalyticsAttributes,
    options?: { persist?: false; useCurrentTrace?: boolean }
): void;
export function addAnalytics(
    analyticsAttributes: AnalyticsAttributes,
    options: { persist?: boolean; useCurrentTrace?: boolean } = {}
) {
    const { persist = false, useCurrentTrace = true } = options;

    const shouldCreateNewSpan = !useCurrentTrace || !parentSpan;

    if (shouldCreateNewSpan) {
        createTrace();
    }

    if (parentSpan === undefined) {
        CoralogixRum.log(
            CoralogixLogSeverity.Warn,
            "Couldn't add analytics, parent span could not be created",
            null,
            analyticsAttributes
        );
        return persist ? { endAnalytics: () => {} } : undefined;
    }

    const { endSpan } = parentSpan.startCustomSpan(InstrumentTraceType.Analytics, {
        ...analyticsAttributes,
        [TraceType]: InstrumentTraceType.Analytics,
    });

    if (persist) {
        return { endAnalytics: endSpan };
    }

    endSpan();
}

export const addLog = (
    logAttributes: LogAttributes,
    options: {
        severity?: LogLevel;
        useCurrentTrace?: boolean;
    } = {}
): void => {
    const { severity = LogLevel.Info, useCurrentTrace = true } = options;

    const shouldCreateNewSpan = !useCurrentTrace || !parentSpan;

    if (shouldCreateNewSpan) {
        createTrace();
    }

    if (parentSpan === undefined) {
        CoralogixRum.log(
            CoralogixLogSeverity.Warn,
            "Couldn't add log, parent span could not be created",
            null,
            logAttributes
        );
        return;
    }

    parentSpan
        .startCustomSpan(InstrumentTraceType.Log, {
            ...logAttributes,
            [LogSeverity]: severity,
            [TraceType]: InstrumentTraceType.Log,
        })
        .endSpan();
};

export const addMetrics = (metrics: MetricAttributes): void => {
    for (const [name, value] of Object.entries(metrics)) {
        CoralogixRum.sendCustomMeasurement(name, value);
    }
};

export function useObservability({
    useCurrentTrace = true,
}: {
    useCurrentTrace?: boolean;
} = {}) {
    useEffect(() => {
        if (!useCurrentTrace) {
            createTrace();
            return () => {
                closeTrace();
            };
        }
    }, [useCurrentTrace]);

    return {
        addAnalytics,
        addLog,
        addMetrics,
        setUserContext,
        updateLabels,
    };
}
